import { Fragment } from 'react';
import clsx from 'clsx';
import type { ChatMessage as ChatMessageType } from '@mise/shared';

interface ChatMessageProps {
  message: ChatMessageType;
}

// The assistant speaks light markdown (**bold** for figures and store names).
// Render that inline instead of printing the asterisks. Keeps line breaks
// (the <p> is whitespace-pre-wrap) — no markdown library needed.
function renderInline(text: string) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}

export default function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.sender === 'user';

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div
      className={clsx(
        'flex flex-col max-w-[85%]',
        isUser ? 'items-end ml-auto' : 'items-start mr-auto'
      )}
    >
      <div
        className={clsx(
          'px-3 py-2 rounded-2xl text-sm',
          isUser
            ? 'bg-primary-600 text-white rounded-br-sm'
            : 'bg-slate-100 text-slate-800 rounded-bl-sm'
        )}
      >
        <p className="whitespace-pre-wrap">{renderInline(message.text)}</p>
      </div>
      <span
        className={clsx(
          'text-xs mt-1 px-1',
          isUser ? 'text-slate-400' : 'text-slate-400'
        )}
      >
        {formatTime(message.timestamp)}
        {message.status === 'sending' && ' ...'}
        {message.status === 'error' && ' (failed)'}
      </span>
    </div>
  );
}
